import {
  DEFAULT_AWS_PRESIGNED_URL_CACHE_FALLBACK_TTL_MS,
  getPresignedUrlCacheExpiresAt,
} from '@main/shared/utils';
import type { ImageDimensionConstraints } from '@main/shared/utils/lexical/image-dimensions';
import type { LexicalEditor } from 'lexical';

export type EditorImageUrlResolver = (fileId: string) => Promise<string | null | undefined>;

export type EditorImageConfig = {
  resolveUrl?: EditorImageUrlResolver;
  dimensions?: ImageDimensionConstraints;
  loadErrorMessage?: string;
  resizeLabel?: string;
};

type EditorImageResolvedUrlCacheEntry = {
  expiresAt: number;
  promise: Promise<string | null | undefined>;
};

export const IMAGE_RESIZE_DRAFT_UPDATE_TAG = 'image-resize-draft';
export const IMAGE_RESIZE_FINAL_UPDATE_TAG = 'image-resize-final';

const editorImageConfigs = new WeakMap<LexicalEditor, EditorImageConfig>();
const editorImagePreviewUrls = new WeakMap<LexicalEditor, Map<string, string>>();
const editorImageResolvedUrls = new WeakMap<
  LexicalEditor,
  Map<string, EditorImageResolvedUrlCacheEntry>
>();

export function setEditorImageConfig(editor: LexicalEditor, config?: EditorImageConfig) {
  if (!config) {
    editorImageConfigs.delete(editor);
    revokeEditorImagePreviewUrls(editor);
    editorImageResolvedUrls.delete(editor);
    return;
  }

  editorImageConfigs.set(editor, config);
}

export function getEditorImageConfig(editor: LexicalEditor): EditorImageConfig | undefined {
  return editorImageConfigs.get(editor);
}

export function setEditorImagePreviewUrl(
  editor: LexicalEditor,
  fileId: string,
  previewUrl: string,
) {
  if (!previewUrl.startsWith('blob:')) {
    return;
  }

  const previewUrls = getEditorImagePreviewUrls(editor);
  const previousPreviewUrl = previewUrls.get(fileId);
  if (previousPreviewUrl) {
    URL.revokeObjectURL(previousPreviewUrl);
  }

  previewUrls.set(fileId, previewUrl);
}

export function getEditorImagePreviewUrl(editor: LexicalEditor, fileId: string) {
  return getEditorImagePreviewUrls(editor).get(fileId);
}

export function resolveEditorImageUrl(editor: LexicalEditor, fileId: string) {
  const config = getEditorImageConfig(editor);
  if (!config?.resolveUrl) {
    return Promise.resolve(null);
  }

  const resolvedUrls = getEditorImageResolvedUrls(editor);
  const now = Date.now();
  const cachedResolvedUrl = resolvedUrls.get(fileId);
  if (cachedResolvedUrl && cachedResolvedUrl.expiresAt > now) {
    return cachedResolvedUrl.promise;
  }

  if (cachedResolvedUrl) {
    resolvedUrls.delete(fileId);
  }

  const resolvedUrl = config
    .resolveUrl(fileId)
    .then((url) => {
      if (!url) {
        resolvedUrls.delete(fileId);
        return url;
      }

      const expiresAt = getPresignedUrlCacheExpiresAt(url);
      if (expiresAt <= Date.now()) {
        resolvedUrls.delete(fileId);
        return url;
      }

      const latestEntry = resolvedUrls.get(fileId);
      if (latestEntry?.promise === resolvedUrl) {
        latestEntry.expiresAt = expiresAt;
      }

      return url;
    })
    .catch(() => {
      resolvedUrls.delete(fileId);
      return null;
    });
  resolvedUrls.set(fileId, {
    expiresAt: now + DEFAULT_AWS_PRESIGNED_URL_CACHE_FALLBACK_TTL_MS,
    promise: resolvedUrl,
  });
  return resolvedUrl;
}

function getEditorImagePreviewUrls(editor: LexicalEditor) {
  const existingPreviewUrls = editorImagePreviewUrls.get(editor);
  if (existingPreviewUrls) {
    return existingPreviewUrls;
  }

  const previewUrls = new Map<string, string>();
  editorImagePreviewUrls.set(editor, previewUrls);

  return previewUrls;
}

function getEditorImageResolvedUrls(editor: LexicalEditor) {
  const existingResolvedUrls = editorImageResolvedUrls.get(editor);
  if (existingResolvedUrls) {
    return existingResolvedUrls;
  }

  const resolvedUrls = new Map<string, EditorImageResolvedUrlCacheEntry>();
  editorImageResolvedUrls.set(editor, resolvedUrls);

  return resolvedUrls;
}

function revokeEditorImagePreviewUrls(editor: LexicalEditor) {
  const previewUrls = editorImagePreviewUrls.get(editor);
  if (!previewUrls) {
    return;
  }

  for (const previewUrl of previewUrls.values()) {
    URL.revokeObjectURL(previewUrl);
  }

  editorImagePreviewUrls.delete(editor);
}
